<template>
  <b-overlay ref="signUpOverlay" :show="loading" variant="white" opacity="0.7">
    <b-form @submit="onSubmit" id="sign-up-form">
      <!-- ### Company ################################################################################ -->
      <b-form-group
          label-size="lg"
          label-class="font-weight-bold pt-0"
      >
        <template v-slot:label>
          <div class="border-bottom">
            {{ $t('signup.company') }}
          </div>
        </template>

        <b-form-group>
          <b-form-checkbox
              v-model="form.is_business_user"
              name="individual-check-button"
              switch
          >
            {{ $t("signup.form.business") }}
          </b-form-checkbox>
        </b-form-group>

        <!-- ### Company name ################################################## -->
        <b-form-group
            v-if="form.is_business_user"
            :label="$t('signup.form.cname.label')"
            id="input-group-1"
            label-for="input-1"
        >
          <b-form-input
              id="input-1"
              autofocus
              :disabled="loading || !form.is_business_user"
              v-model="$v.form.company_name.$model"
              :placeholder="$t('signup.form.cname.ph')"
              :state="validateState('company_name')"
          ></b-form-input>
          <b-form-invalid-feedback>{{
              $t("form.required")
            }}
          </b-form-invalid-feedback>
          <div class="mt-2">
            <b-icon-exclamation-circle-fill variant="warning"></b-icon-exclamation-circle-fill>
            {{ $t('signup.form.business_reg_name_note') }}
          </div>
        </b-form-group>

        <!-- ### DBA ################################################## -->
        <b-form-group
            v-if="form.is_business_user"
            id="input-group-2"
            :label="$t('signup.form.dba.label')"
            label-for="input-2"
        >
          <b-form-input
              id="input-2"
              :disabled="loading || !form.is_business_user"
              v-model="$v.form.dba.$model"
              :placeholder="$t('signup.form.dba.ph')"
              :state="validateState('dba')"
          ></b-form-input>
          <b-form-invalid-feedback>{{
              $t("form.required")
            }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form-group>

      <!-- ### Address ################################################################################ -->
      <b-form-group
          v-if="USER_ADDRESS"
          label-size="lg"
          label-class="font-weight-bold pt-0"
      >
        <template v-slot:label>
          <template v-if="USER_ADDRESS_OPTIONAL">
            <div class="text-left border-bottom" type="button" v-on:click="addressVisible = !addressVisible" data-toggle="collapse" data-target="#address-pane" aria-expanded="true" aria-controls="address-pane">
              <div class="d-flex flex-row justify-content-between">
                <div class="font-weight-bold col-form-label-lg p-0">
                  {{ $t('signup.addr') }}
                  <span v-if="USER_ADDRESS_OPTIONAL" class="text-secondary"> ({{ $t('msg.optional') }})</span>
                </div>
                <div v-if="USER_ADDRESS_OPTIONAL" class="d-flex align-items-center">
                  <b-icon-chevron-compact-down v-if="addressVisible" style="opacity: 0.75"></b-icon-chevron-compact-down>
                  <b-icon-chevron-compact-up v-else style="opacity: 0.75"></b-icon-chevron-compact-up>
                </div>
              </div>
            </div>
<!--
            <div class="accordion" id="accordionExample">
              <div class="card">
                <div class="card-header p-0" id="headingOne">
                    <button class="btn btn-link btn-block text-left" type="button" data-toggle="collapse" data-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                      <div class="d-flex flex-row justify-content-between">
                        <div class="font-weight-bold col-form-label-lg">
                          {{ $t('signup.addr') }}
                          <span v-if="USER_ADDRESS_OPTIONAL" class="text-secondary"> ({{ $t('msg.optional') }})</span>
                        </div>
                        <div v-if="USER_ADDRESS_OPTIONAL">
                          <b-icon-chevron-double-up></b-icon-chevron-double-up>
                        </div>
                      </div>
                    </button>
                </div>

                <div id="collapseOne" class="collapse show" aria-labelledby="headingOne" data-parent="#accordionExample">
                  <div class="card-body">
                    Anim pariatur cliche reprehenderit, enim eiusmod high life accusamus terry richardson ad squid. 3 wolf moon officia aute, non cupidatat skateboard dolor brunch. Food truck quinoa nesciunt laborum eiusmod. Brunch 3 wolf moon tempor, sunt aliqua put a bird on it squid single-origin coffee nulla assumenda shoreditch et. Nihil anim keffiyeh helvetica, craft beer labore wes anderson cred nesciunt sapiente ea proident. Ad vegan excepteur butcher vice lomo. Leggings occaecat craft beer farm-to-table, raw denim aesthetic synth nesciunt you probably haven't heard of them accusamus labore sustainable VHS.
                  </div>
                </div>
              </div>
            </div>
            -->
          </template>
          <template v-else>
            {{ $t('signup.addr') }}
          </template>
        </template>

        <b-collapse id="address-pane" :visible="!USER_ADDRESS_OPTIONAL || addressVisible">
          <!-- ### Address 1 ################################################## -->
          <b-form-group
              id="input-group-3"
              :label="$t('signup.form.address.label')"
              label-for="input-3"
          >
            <b-form-input
                id="input-3"
                :disabled="loading"
                v-model="$v.form.address.$model"
                :placeholder="$t('signup.form.address.ph')"
                :state="validateState('address')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- ### Address 2 ################################################## -->
          <b-form-group
              id="input-group-4"
              :label="$t('signup.form.address2.label')"
              label-for="input-4"
          >
            <b-form-input
                id="input-4"
                :disabled="loading"
                v-model="$v.form.address2.$model"
                :placeholder="$t('signup.form.address2.ph')"
                :state="validateState('address2')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-row>
            <b-col cols="12" sm="6">
              <!-- ### City ################################################## -->
              <b-form-group
                  id="input-group-5"
                  :label="$t('signup.form.city.label')"
                  label-for="input-5"
              >
                <b-form-input
                    id="input-5"
                    :disabled="loading"
                    v-model="$v.form.city.$model"
                    :placeholder="$t('signup.form.city.ph')"
                    :state="validateState('city')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Region ################################################## -->
              <b-form-group
                  id="input-group-6"
                  :label="$t('signup.form.region.label')"
                  label-for="input-6"
              >
                <b-form-input
                    id="input-6"
                    :disabled="loading"
                    v-model="$v.form.region.$model"
                    :placeholder="$t('signup.form.region.ph')"
                    :state="validateState('region')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" sm="6">
              <!-- ### Postal Code ################################################## -->
              <b-form-group
                  id="input-group-7"
                  :label="$t('signup.form.zip.label')"
                  label-for="input-7"
              >
                <b-form-input
                    id="input-7"
                    :disabled="loading"
                    v-model="$v.form.postal_code.$model"
                    :placeholder="$t('signup.form.zip.ph')"
                    :state="validateState('postal_code')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Country ################################################## -->
              <b-form-group
                  id="input-group-8"
                  :label="$t('signup.form.country.label')"
                  label-for="input-8"
              >
                <b-form-select
                    id="input-8"
                    :disabled="loading || countriesLoading"
                    v-model="$v.form.country.$model"
                    :placeholder="$t('signup.form.country.ph')"
                    :state="validateState('country')"
                    :options="$store.state.countries.items"
                >
                  <b-spinner small></b-spinner>
                </b-form-select>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-collapse>
      </b-form-group>

      <b-form-group
          label-size="lg"
          label-class="font-weight-bold pt-0"
          class="mb-0"
      >
        <template v-slot:label>
          <div class="border-bottom">
            {{ $t('signup.user') }}
          </div>
        </template>

        <b-form-row>
          <b-col cols="12" sm="6">
            <!-- ### First name ################################################## -->
            <b-form-group
                id="input-group-10"
                :label="$t('signup.form.fname.label')"
                label-for="input-10"
                class="mb-2"
            >
              <b-form-input
                  id="input-10"
                  :disabled="loading"
                  v-model="$v.form.first_name.$model"
                  :placeholder="$t('signup.form.fname.ph')"
                  :state="validateState('first_name')"
              ></b-form-input>
              <b-form-invalid-feedback>{{
                  $t("form.required")
                }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6">
            <!-- ### Last name ################################################## -->
            <b-form-group
                id="input-group-11"
                :label="$t('signup.form.lname.label')"
                label-for="input-11"
                class="mb-2"
            >
              <b-form-input
                  id="input-11"
                  :disabled="loading"
                  v-model="$v.form.last_name.$model"
                  :placeholder="$t('signup.form.lname.ph')"
                  :state="validateState('last_name')"
              ></b-form-input>
              <b-form-invalid-feedback>{{
                  $t("form.required")
                }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-form-row>
        <div v-if="!form.is_business_user" class="mb-3">
          <b-icon-exclamation-circle-fill variant="warning"></b-icon-exclamation-circle-fill>
          {{ $t('signup.form.individual_reg_name_note') }}
        </div>

        <!-- ### Email ################################################## -->
        <b-form-group
            id="input-group-12"
            :label="$t('signup.form.email.label')"
            label-for="input-12"
        >
          <b-form-input
              id="input-12"
              :disabled="loading"
              v-model="$v.form.email.$model"
              type="email"
              autocomplete="off"
              :placeholder="$t('signup.form.email.ph')"
              :state="validateState('email')"
          ></b-form-input>
          <b-form-invalid-feedback>{{
              $t("form.validEmail")
            }}
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- ### Password ################################################## -->
        <b-form-group
            id="input-group-13"
            :label="$t('signup.form.pass.label')"
            label-for="input-13"
        >
          <b-form-row>
            <b-col cols="12" sm="6" class="mb-sm-0 mb-3">
              <b-form-input
                  id="input-13"
                  :disabled="loading"
                  v-model="$v.form.password.$model"
                  :placeholder="$t('signup.form.pass.ph')"
                  :state="validateState('password')"
                  type="password"
                  autocomplete="new-password"
              ></b-form-input>
              <b-form-invalid-feedback>{{
                  $t("form.passwordReqLen", [6])
                }}
              </b-form-invalid-feedback>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Password 2 ################################################ -->
              <b-form-input
                  id="input-14"
                  :disabled="loading"
                  v-model="$v.form.password2.$model"
                  :placeholder="$t('signup.form.pass.ph2')"
                  :state="validateState('password2')"
                  type="password"
              ></b-form-input>
              <b-form-invalid-feedback>{{
                  $t("form.passwordsDiff")
                }}
              </b-form-invalid-feedback>
            </b-col>
          </b-form-row>
        </b-form-group>

        <!-- ### Remember me ################################################## -->
        <b-form-group id="input-group-15">
          <b-form-checkbox v-model="form.remember" :disabled="loading" switch>{{
              $t("login.form.remember")
            }}
          </b-form-checkbox>
        </b-form-group>
      </b-form-group>

      <div class="mt-3 mb-2">
        <i18n path="signup.legal.agreement" tag="label">
          <a :href="URL_TERMS_OS_SERVICE" target="_blank">{{ $t('signup.legal.terms') }}</a>
          <a :href="URL_PRIVACY_POLICY" target="_blank">{{ $t('signup.legal.policy') }}</a>
        </i18n>
      </div>

      <b-container class="p-0">
        <b-row>
          <b-col sm="6">
            <b-button
                type="submit"
                variant="primary"
                class="w-100 mt-3"
                :disabled="loading"
            >{{ $t("signup.form.button") }}
            </b-button
            >
          </b-col>
        </b-row>
      </b-container>
    </b-form>

    <!-- ### Login ###################################### -->
    <div class="router" ref="container">
      <router-link to="/sign-in">{{ $t("signup.login") }}</router-link>
    </div>
  </b-overlay>
</template>

<script>
// @ is an alias to /src
import {validationMixin} from "vuelidate";
import {required, email, sameAs, minLength} from "vuelidate/lib/validators";
import {
  BIconChevronCompactUp,
  BIconChevronCompactDown,
  BIconExclamationCircleFill
} from "bootstrap-vue";
import {
  LOAD_COUNTRIES,
  SETUP_FROM_DATA,
  REMEMBER_ME,
  SIGN_OUT,
} from "@/store/consts";
import {MIN_PASSWORD_LENGTH, URL_REGISTER_NEW_CLIENT, USER_ADDRESS, USER_ADDRESS_OPTIONAL, URL_TERMS_OS_SERVICE, URL_PRIVACY_POLICY} from "@/globals";

export default {
  name: "SignUpForm",
  mixins: [validationMixin],
  components: {
    BIconChevronCompactUp,
    BIconChevronCompactDown,
    BIconExclamationCircleFill
  },
  data() {
    return {
      form: {
        is_business_user: true,
        company_name: "",
        dba: "",
        address: "",
        address2: "",
        city: "",
        region: "",
        postal_code: "",
        country: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        password2: "",
        remember: false,
      },
      loading: false,
      countriesLoading: false,
      user: null,
      addressVisible: false
    };
  },
  validations() {
    let result = {
      form: {},
    };
    if (this.form.is_business_user) {
      result.form.company_name = {}; //{required};
    } else {
      result.form.company_name = {};
    }
    result.form.dba = {};

    result.form.address = {}; //this.USER_ADDRESS && !this.USER_ADDRESS_OPTIONAL ? {required} : {};
    result.form.address2 = {};
    result.form.city = {}; //this.USER_ADDRESS && !this.USER_ADDRESS_OPTIONAL ? {required} : {};
    result.form.region = {}; //this.USER_ADDRESS && !this.USER_ADDRESS_OPTIONAL ? {required} : {};
    result.form.postal_code = {}; //this.USER_ADDRESS && !this.USER_ADDRESS_OPTIONAL ? {required} : {};
    result.form.country = {}; //this.USER_ADDRESS && !this.USER_ADDRESS_OPTIONAL ? {required} : {};

    result.form.first_name = {}; //{required};
    result.form.last_name = {}; //{required};
    result.form.email = {required, email};
    result.form.password = {
      required,
      minLength: minLength(MIN_PASSWORD_LENGTH),
    };
    result.form.password2 = {required, sameAsPassword: sameAs("password")};
    return result;
  },
  methods: {
    validateState(name) {
      const {$dirty, $error} = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit(evt) {
      evt.preventDefault();
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.$store.commit(SIGN_OUT);
      this.$post(
          URL_REGISTER_NEW_CLIENT,
          {
            is_individual: !this.form.is_business_user,
            company: this.form.company_name,
            dba: this.form.dba,
            address: this.form.address,
            address2: this.form.address2,
            city: this.form.city,
            region: this.form.region,
            postal_code: this.form.postal_code,
            country: this.form.country,
            user: {
              first_name: this.form.first_name,
              last_name: this.form.last_name,
              email: this.form.email,
              login: "",
              password: this.form.password,
              // При регистрации нового пользователя локаль можно взять только из this.$i18n.locale
              locale: this.$i18n.locale,
            },
          },
          true
      )
          .then((result) => {
            console.log(result);
            // Запомним чекбокс "запомнить меня" чтобы после подтверждения кодом сохранить токен в localStorage
            if (this.form.remember) this.$store.commit(REMEMBER_ME);
            this.$store.dispatch(SETUP_FROM_DATA, result.user);
            this.$router.push({
              name: "sign-up-verification",
              params: {email: result.user.emails[0]},
            });
          })
          .catch((error) => {
            this.$alert.show(error);
          });
    },
  },
  mounted() {
    this.$store.dispatch(LOAD_COUNTRIES);
  },
  created() {
    this.USER_ADDRESS = USER_ADDRESS
    this.USER_ADDRESS_OPTIONAL = USER_ADDRESS_OPTIONAL
    this.URL_TERMS_OS_SERVICE = URL_TERMS_OS_SERVICE
    this.URL_PRIVACY_POLICY = URL_PRIVACY_POLICY
  }
};
</script>

<style>
#sign-up-form .col-form-label-lg {
  padding-bottom: 1em;
}
</style>